import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { BookOpen, Sparkles, Globe, Check } from 'lucide-react';
import PageHeader from './PageHeader';

const Pricing = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tiers = [
    {
      name: 'Interactive Story DIY',
      price: 'Free',
      priceDetail: 'Forever',
      description: 'Perfect for hobbyists and personal projects',
      color: 'bg-slate-600',
      borderColor: 'border-slate-300',
      icon: BookOpen,
      features: [
        'Access to Easy Story Builder (Compiler)',
        'Sample stories to learn from',
        'Basic export formats',
        'Community support'
      ],
      cta: 'Get Started Free',
      ctaPath: '/examples',
      ctaStyle: 'bg-slate-600 hover:bg-slate-700'
    },
    {
      name: 'PRO',
      price: '$',
      priceDetail: 'Coming Soon',
      description: 'For serious creators who need advanced tools',
      color: 'bg-purple-600',
      borderColor: 'border-purple-400',
      icon: Sparkles,
      popular: true,
      features: [
        'Everything in DIY, plus:',
        'Visual Editor for node-based story building',
        'Visual Story Planner with AI assistance',
        'Shared Story sessions (live collaboration)',
        'Premium export formats',
        'Priority support'
      ],
      cta: 'Explore PRO Features',
      ctaPath: '/pro',
      ctaStyle: 'bg-purple-600 hover:bg-purple-700'
    },
    {
      name: 'Agency',
      price: '$$',
      priceDetail: 'Coming Soon',
      description: 'For teams building client experiences at scale',
      color: 'bg-amber-600',
      borderColor: 'border-amber-400',
      icon: Globe,
      features: [
        'Everything in PRO, plus:',
        'Sitemap to PathNav conversion',
        'Customer Journey Wizard',
        'White-label export options',
        'Team collaboration tools',
        'Dedicated support'
      ],
      cta: 'Explore Agency Features',
      ctaPath: '/agency',
      ctaStyle: 'bg-amber-600 hover:bg-amber-700'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PageHeader />

      {/* Page headline */}
      <section className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
          Paid Tier Options
        </h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto">
          Choose the plan that fits your storytelling needs. Start free and upgrade as you grow.
        </p>
      </section>

      {/* Pricing Cards */}
      <section className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div 
              key={tier.name}
              className={`relative bg-white rounded-xl shadow-lg border-2 ${tier.borderColor} p-6 flex flex-col ${tier.popular ? 'ring-2 ring-purple-400' : ''}`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </span>
                </div>
              )}
              
              {/* Icon */}
              <div className={`w-12 h-12 ${tier.color} rounded-lg flex items-center justify-center mb-4`}>
                <tier.icon className="h-6 w-6 text-white" />
              </div>
              
              {/* Name & Price */}
              <h2 className="text-2xl font-bold text-slate-900 mb-1">{tier.name}</h2>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl font-bold text-slate-900">{tier.price}</span>
                <span className="text-slate-500 text-sm">{tier.priceDetail}</span>
              </div>
              <p className="text-slate-600 text-sm mb-6">{tier.description}</p>
              
              {/* Features */}
              <ul className="space-y-3 mb-6 flex-grow">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              {/* CTA */}
              <Button 
                onClick={() => navigate(tier.ctaPath)}
                className={`w-full ${tier.ctaStyle} text-white`}
              >
                {tier.cta}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Note */}
      <section className="container mx-auto px-4 py-12 text-center">
        <p className="text-slate-500">
          Have questions? Check out our <a href="/#faq" className="text-indigo-600 hover:underline">FAQ</a> or contact us.
        </p>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 border-t border-slate-200">
        <div className="text-center">
          <p className="text-slate-600">© 2025 TweePorter.ca Website. Built with care for storytellers.</p>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
